import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { WeekNav } from '../WeekNav';
import { formatRemaining, nextDailyResetUtc, nextWeeklyResetUtc } from '../../lib/period';

interface AccountOption {
  accountId: number;
  label: string;
}

interface ChecklistHeaderProps {
  accounts: AccountOption[];
  selectedAccountId: number | null;
  onSelectAccount: (accountId: number) => void;
  weekLabel: string;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onThisWeek: () => void;
  isCurrentWeek: boolean;
  canGoNext: boolean;
}

const FINAL_HOUR_MS = 60 * 60 * 1000;

/** Live UTC countdowns to the next daily and weekly reset — ticks client-side, no server round trip. */
function LiveCountdowns() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const localHint = (target: Date) =>
    `resets ${target.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} your time`;

  const dailyTarget = nextDailyResetUtc(now);
  const weeklyTarget = nextWeeklyResetUtc(now);
  const dailyRemaining = dailyTarget.getTime() - now.getTime();
  const weeklyRemaining = weeklyTarget.getTime() - now.getTime();

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-end leading-tight" title={localHint(dailyTarget)}>
        <span className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.13em] text-text-faint">
          Daily
        </span>
        <span
          className={[
            'text-sm font-semibold tabular-nums',
            dailyRemaining <= FINAL_HOUR_MS ? 'text-warning' : 'text-accent-strong',
          ].join(' ')}
        >
          {formatRemaining(dailyRemaining)}
        </span>
      </div>
      <div className="flex flex-col items-end leading-tight" title={localHint(weeklyTarget)}>
        <span className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.13em] text-text-faint">
          Weekly (Thu)
        </span>
        <span
          className={[
            'text-sm font-semibold tabular-nums',
            weeklyRemaining <= FINAL_HOUR_MS ? 'text-warning' : 'text-crystal',
          ].join(' ')}
        >
          {formatRemaining(weeklyRemaining)}
        </span>
      </div>
    </div>
  );
}

export function ChecklistHeader({
  accounts,
  selectedAccountId,
  onSelectAccount,
  weekLabel,
  onPrevWeek,
  onNextWeek,
  onThisWeek,
  isCurrentWeek,
  canGoNext,
}: ChecklistHeaderProps) {
  return (
    <div className="space-y-4 rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-semibold text-text">Checklist</h1>
          <p className="mt-1 text-sm text-text-muted">Daily, weekly and monthly tasks, all in one place.</p>
        </div>
        <div className="flex items-center gap-4">
          <LiveCountdowns />
          <Link
            to="/checklist/manage"
            className="min-h-tap rounded-lg border border-border-strong px-3 py-2 text-sm font-semibold text-text-muted transition-colors hover:border-accent hover:text-accent"
          >
            Manage tasks
          </Link>
        </div>
      </div>

      {accounts.length > 1 && (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Account">
          {accounts.map((account) => (
            <button
              key={account.accountId}
              type="button"
              role="tab"
              aria-selected={account.accountId === selectedAccountId}
              onClick={() => onSelectAccount(account.accountId)}
              className={[
                'min-h-tap rounded-full px-4 text-sm font-semibold transition-colors',
                account.accountId === selectedAccountId
                  ? 'bg-accent text-accent-contrast'
                  : 'bg-bg-elevated-hover text-text-muted hover:bg-border-strong',
              ].join(' ')}
            >
              {account.label}
            </button>
          ))}
        </div>
      )}

      <WeekNav
        weekLabel={weekLabel}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
        onThisWeek={onThisWeek}
        isCurrentWeek={isCurrentWeek}
        canGoNext={canGoNext}
      />
    </div>
  );
}
